/**
 * AtlasProbeRunnerModule — agent-runnable in-page probe runner (`?probe=…`).
 *
 * Built for browser agents (Claude in Chrome) and humans alike: navigate to
 *   http://localhost:5180/?perf=1&probe=all        (or a comma list:
 *   ?probe=perf,tiers · add &probeFast=1 for short windows)
 * and the runner auto-creates an account if needed, clicks through the main
 * menu, waits for the LOADING GATE (movement — and every physics/lag metric —
 * is gated on it; the law that cost two headless probe runs), executes the
 * suites, and renders every result into a fixed DOM overlay:
 *   #probe-status  one-line state (RUNNING → PASS/FAIL)
 *   #probe-log     human-readable lines + tables
 *   #probe-json    the full machine-readable report (also console.log'd)
 * The overlay is pointer-events:none — it can never block the game.
 *
 * Inert without the URL param: registration costs one string check per boot.
 * ?perf=1 alongside enables GPU timestamp queries (real GPU frame ms).
 * Deliberately does NOT set atlas:probe (inspection lighting distorts perf).
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { SUITES, ALL_ORDER, type ProbeCtx, type FrameReport } from '../probe/probe_suites.js';

export class AtlasProbeRunnerModule implements IModule {
  readonly name = 'AtlasProbeRunner';

  private app: App | null = null;
  private panel: HTMLElement | null = null;
  private statusEl: HTMLElement | null = null;
  private logEl: HTMLElement | null = null;
  private bootMarks: Record<string, number> = { init: performance.now() };
  private frames: number[] = [];
  private sampling = false;
  private longs: number[] = [];
  private intentTimer: number | null = null;
  private checksPassed = 0;
  private checksFailed = 0;

  init(app: App): void {
    const suitesParam = new URLSearchParams(location.search).get('probe');
    // Boot marks are cheap and useful even when inert? No — record only when armed.
    if (!suitesParam) return;
    this.app = app;
    bus.on('atlas:account-ready', () => { this.bootMarks.accountReady ??= performance.now(); });
    bus.on('atlas:world-entered', () => { this.bootMarks.worldEntered ??= performance.now(); });
    bus.on('atlas:loading-done', () => { this.bootMarks.gateDrop ??= performance.now(); });
    this.buildOverlay();
    const wanted = suitesParam === 'all' ? ALL_ORDER : suitesParam.split(',').map((s) => s.trim()).filter((s) => s in SUITES);
    if (wanted.length === 0) { this.setStatus(`unknown suites "${suitesParam}" — valid: all, ${Object.keys(SUITES).join(', ')}`, 'var(--ax-bad)'); return; }
    this.setStatus(`ARMED — suites: ${wanted.join(', ')} · waiting for boot`, 'var(--ax-warn)');
    // rAF sampler runs for the page's life; costs one push per frame while sampling.
    const tick = (t: number): void => { if (this.sampling) this.frames.push(t - this.lastT); this.lastT = t; requestAnimationFrame(tick); };
    requestAnimationFrame(tick);
    try {
      new PerformanceObserver((list) => { for (const e of list.getEntries()) this.longs.push(e.duration); }).observe({ entryTypes: ['longtask'] });
    } catch { /* longtask API absent */ }
    void this.run(wanted);
  }

  private lastT = performance.now();

  // ── overlay ──────────────────────────────────────────────────────────────────
  private buildOverlay(): void {
    const p = document.createElement('div');
    p.id = 'probe-panel';
    p.style.cssText = 'position:fixed;top:8px;right:8px;width:500px;max-height:86vh;overflow:auto;z-index:99999;'
      + 'background:color-mix(in srgb, var(--ax-bg) 88%, transparent);color:var(--ax-text);font:11px/1.45 var(--ax-mono);padding:10px 12px;'
      + 'border:1px solid var(--ax-line);border-radius:var(--ax-radius);pointer-events:none;white-space:pre-wrap;word-break:break-word;';
    const s = document.createElement('div');
    s.id = 'probe-status';
    s.style.cssText = 'font-weight:bold;font-size:13px;margin-bottom:6px;color:var(--ax-warn);';
    const l = document.createElement('div');
    l.id = 'probe-log';
    p.append(s, l);
    document.body.appendChild(p);
    this.panel = p; this.statusEl = s; this.logEl = l;
    document.title = `PROBE · ${document.title}`;
  }

  private setStatus(text: string, color = 'var(--ax-warn)'): void {
    if (this.statusEl) { this.statusEl.textContent = text; this.statusEl.style.color = color; }
  }

  private log(line: string): void {
    if (!this.logEl) return;
    const d = document.createElement('div');
    d.textContent = line;
    this.logEl.appendChild(d);
    this.panel?.scrollTo(0, this.panel.scrollHeight);
  }

  private section(title: string): void {
    if (!this.logEl) return;
    const d = document.createElement('div');
    d.textContent = `── ${title} ──`;
    d.style.cssText = 'margin-top:8px;font-weight:bold;color:var(--ax-soft);';
    this.logEl.appendChild(d);
  }

  private table(title: string, rows: Record<string, Record<string, unknown>>): void {
    if (!this.logEl) return;
    const cols = [...new Set(Object.values(rows).flatMap((r) => Object.keys(r)))];
    const t = document.createElement('table');
    // width:max-content + panel overflow:auto — wide tables scroll instead of
    // clipping their last column (the real-GPU tiers run lost 'mtris').
    t.style.cssText = 'border-collapse:collapse;margin:4px 0;width:max-content;';
    const mk = (tag: string, txt: string, bold = false): HTMLElement => {
      const el = document.createElement(tag);
      el.textContent = txt;
      el.style.cssText = `border:1px solid var(--ax-line);padding:1px 5px;text-align:right;white-space:nowrap;${bold ? 'color:var(--ax-soft);' : ''}`;
      return el;
    };
    const head = document.createElement('tr');
    head.append(mk('th', title, true), ...cols.map((c) => mk('th', c, true)));
    t.appendChild(head);
    for (const [name, r] of Object.entries(rows)) {
      const tr = document.createElement('tr');
      tr.append(mk('td', name, true), ...cols.map((c) => mk('td', String(r[c] ?? ''))));
      t.appendChild(tr);
    }
    this.logEl.appendChild(t);
    this.panel?.scrollTo(0, this.panel.scrollHeight);
  }

  private check(label: string, ok: boolean, detail = ''): boolean {
    if (ok) this.checksPassed++; else this.checksFailed++;
    this.log(`${ok ? '✓' : '✗ FAIL'} ${label}${!ok && detail ? ` — ${detail}` : ''}`);
    return ok;
  }

  // ── shared samplers/actions ──────────────────────────────────────────────────
  private sleep(ms: number): Promise<void> { return new Promise((r) => window.setTimeout(r, ms)); }

  private gpuFrameMs(): number | null {
    try {
      // Primary: the value AtlasPerfHudModule's per-frame resolver publishes
      // (resolveTimestampsAsync → info.render.timestamp). The engine gpuTiming
      // stats map is NOT populated on this path — reading it returned n/a on
      // every real-GPU run while timestampFeature/gpuTimingWired were true.
      const native = (this.app as unknown as { renderer: { native: { info?: { render?: { timestamp?: number } } } } }).renderer.native;
      const ts = native.info?.render?.timestamp;
      if (typeof ts === 'number' && ts > 0) return ts;
      const r = (this.app as unknown as { renderer: { gpuTiming?: { getFrameStats?: () => Map<string, { lastMs?: number }> } } }).renderer;
      const v = r.gpuTiming?.getFrameStats?.()?.get('frame')?.lastMs;
      return typeof v === 'number' && v > 0 ? v : null;
    } catch { return null; }
  }

  private async measureFrames(ms: number): Promise<FrameReport> {
    this.frames = []; this.longs = [];
    const gpu: number[] = [];
    this.sampling = true;
    const t0 = performance.now();
    while (performance.now() - t0 < ms) { await this.sleep(400); const v = this.gpuFrameMs(); if (v !== null) gpu.push(v); }
    this.sampling = false;
    const arr = this.frames;
    const pct = (p: number): number => { if (!arr.length) return 0; const s = [...arr].sort((a, b) => a - b); return s[Math.min(s.length - 1, Math.floor(p * s.length))]; };
    const gpct = (p: number): number => { const s = [...gpu].sort((a, b) => a - b); return s[Math.min(s.length - 1, Math.floor(p * s.length))]; };
    return {
      n: arr.length,
      medMs: +pct(0.5).toFixed(1), p95Ms: +pct(0.95).toFixed(1), p99Ms: +pct(0.99).toFixed(1), maxMs: +Math.max(0, ...arr).toFixed(1),
      fps: arr.length ? +(1000 / (arr.reduce((s, v) => s + v, 0) / arr.length)).toFixed(1) : 0,
      hitch50: arr.filter((v) => v > 50).length, hitch100: arr.filter((v) => v > 100).length,
      longCount: this.longs.length, longTotalMs: +this.longs.reduce((s, v) => s + v, 0).toFixed(0), longMaxMs: +Math.max(0, ...this.longs).toFixed(0),
      gpuMedMs: gpu.length ? +gpct(0.5).toFixed(2) : null, gpuMaxMs: gpu.length ? +Math.max(...gpu).toFixed(2) : null,
    };
  }

  private holdIntent(dir: [number, number] | null): void {
    if (this.intentTimer !== null) { window.clearInterval(this.intentTimer); this.intentTimer = null; }
    if (dir) {
      this.intentTimer = window.setInterval(() => { atlas.intentX = dir[0]; atlas.intentZ = dir[1]; }, 50);
    } else { atlas.intentX = 0; atlas.intentZ = 0; }
  }

  private async teleport(x: number, z: number): Promise<boolean> {
    const conn = atlas.conn;
    if (!conn?.identity) return false;
    try { await conn.reducers.debugTeleport({ posX: x, posZ: z }); } catch { return false; }
    for (let i = 0; i < 100; i++) {
      const r = conn.db.playerState.identity.find(conn.identity);
      if (r && Math.abs(r.posX - x) < 0.5 && Math.abs(r.posZ - z) < 0.5) break;
      await this.sleep(100);
    }
    // Adopt locally too: the probe must not wait a reconcile round-trip.
    const seatFn = (globalThis as unknown as { __physSeat?: (x: number, z: number) => boolean }).__physSeat;
    if (seatFn) seatFn(x, z); else { atlas.localX = x; atlas.localZ = z; }
    return true;
  }

  // ── orchestration ────────────────────────────────────────────────────────────
  private async run(wanted: string[]): Promise<void> {
    const fast = new URLSearchParams(location.search).get('probeFast') === '1';
    // 1. Connection + account.
    this.setStatus('BOOT — waiting for connection…');
    for (let i = 0; i < 300 && !atlas.conn?.identity; i++) await this.sleep(1000);
    const conn = atlas.conn;
    if (!conn?.identity) { this.setStatus('FAIL — no connection', 'var(--ax-bad)'); return; }
    if (!conn.db.account.identity.find(conn.identity)) {
      this.log('no account — creating a probe account');
      try { await conn.reducers.createAccount({ username: `probe_${Math.random().toString(36).slice(2, 8)}`, displayName: 'Probe' }); } catch { /* races are fine */ }
      for (let i = 0; i < 50 && !conn.db.account.identity.find(conn.identity); i++) await this.sleep(200);
    }
    // 2. Menu click-through (retry loop — one-shot clicks were the boot killer).
    this.setStatus('BOOT — entering the Commons…');
    for (let i = 0; i < 200 && atlas.currentWorldId === null; i++) {
      (document.getElementById('menu-enter') as HTMLButtonElement | null)?.click();
      (document.getElementById('menu-enter-commons') as HTMLButtonElement | null)?.click();
      (document.getElementById('traveler-begin') as HTMLButtonElement | null)?.click();
      await this.sleep(2000);
    }
    if (atlas.currentWorldId === null) { this.setStatus('FAIL — never entered a world', 'var(--ax-bad)'); return; }
    // 3. The loading gate — movement (and every physics/lag metric) waits on it.
    this.setStatus('BOOT — waiting for the loading gate…');
    for (let i = 0; i < 400 && atlas.loadingGate !== false; i++) await this.sleep(1000);
    if (atlas.loadingGate !== false) { this.setStatus('FAIL — loading gate never dropped', 'var(--ax-bad)'); return; }
    this.bootMarks.gateDrop ??= performance.now();
    await this.sleep(3000);

    // Deterministic NOON (the verify:perf law): a sweep once ran at Atlas
    // night — half the viewport was starfield, terrain unlit black, and every
    // visual/luma judgment was ambiguous. Frozen noon makes runs comparable.
    try { (globalThis as unknown as { atlas?: { setDebugTod?: (v: number) => void } }).atlas?.setDebugTod?.(0.5); this.log('time-of-day pinned to noon (setDebugTod 0.5)'); } catch { /* debug hook absent */ }

    // Environment context ONCE for every run — the tiers run came back with
    // gpu=n/a and no backend recorded because only the perf suite logged it.
    const native = (this.app as unknown as { renderer: { native: { backend?: { constructor?: { name?: string }; hasFeature?: (f: string) => boolean }; getPixelRatio?: () => number } } }).renderer.native;
    const backend = native.backend?.constructor?.name ?? '?';
    const tsFeature = native.backend?.hasFeature?.('timestamp-query') === true || native.backend?.hasFeature?.('EXT_disjoint_timer_query_webgl2') === true;
    const gpuTimingWired = ((): boolean => { try { return !!(this.app as unknown as { renderer: { gpuTiming?: unknown } }).renderer.gpuTiming; } catch { return false; } })();
    this.log(`env: backend=${backend} · pixelRatio=${native.getPixelRatio?.() ?? '?'} · dpr=${window.devicePixelRatio} · timestampFeature=${tsFeature} · gpuTimingWired=${gpuTimingWired}${gpuTimingWired ? '' : ' (gpu columns will be n/a)'}`);

    // 4. Suites.
    const report: Record<string, unknown> = {
      url: location.href, fast, startedAt: new Date().toISOString(),
      suitesRequested: wanted, suites: {} as Record<string, unknown>,
      env: { backend, pixelRatio: native.getPixelRatio?.() ?? null, dpr: window.devicePixelRatio, timestampFeature: tsFeature, gpuTimingWired },
    };
    const ctxBase = {
      log: (l: string) => this.log(l),
      section: (t: string) => this.section(t),
      check: (l: string, ok: boolean, d?: string) => this.check(l, ok, d),
      table: (t: string, r: Record<string, Record<string, unknown>>) => this.table(t, r),
      sleep: (ms: number) => this.sleep(ms),
      teleport: (x: number, z: number) => this.teleport(x, z),
      measureFrames: (ms: number) => this.measureFrames(ms),
      holdIntent: (d: [number, number] | null) => this.holdIntent(d),
      fast,
      bootMarks: this.bootMarks,
      rendererNative: () => (this.app as unknown as { renderer: { native: unknown } }).renderer.native,
    };
    for (const name of wanted) {
      this.setStatus(`RUNNING — ${name} (${wanted.indexOf(name) + 1}/${wanted.length}) · ✓${this.checksPassed} ✗${this.checksFailed}`);
      const suiteReport: Record<string, unknown> = {};
      (report.suites as Record<string, unknown>)[name] = suiteReport;
      const ctx: ProbeCtx = { ...ctxBase, report: suiteReport };
      try {
        await SUITES[name](ctx);
      } catch (err) {
        this.check(`${name}: suite completed`, false, String(err).slice(0, 160));
        suiteReport.error = String(err).slice(0, 300);
      }
      this.holdIntent(null);
    }

    // 5. Verdict + machine-readable report.
    report.checks = { passed: this.checksPassed, failed: this.checksFailed };
    const pass = this.checksFailed === 0;
    // Phrased as a statement, not an instruction — an agent reading the panel
    // asked whether it should reload; the panel must never direct its reader.
    this.setStatus(`${pass ? 'PROBE PASS' : 'PROBE FAIL'} — ✓${this.checksPassed} ✗${this.checksFailed} (fixtures/teleports persist until the page reloads)`, pass ? 'var(--ax-good)' : 'var(--ax-bad)'); // the pass tone is the theme's (gaps wave 2026-09-28: a JS-value literal the scan could not see)
    this.section('machine-readable report');
    const pre = document.createElement('pre');
    pre.id = 'probe-json';
    pre.style.cssText = 'white-space:pre-wrap;word-break:break-all;color:var(--ax-soft);font-size:10px;';
    pre.textContent = JSON.stringify(report);
    this.logEl?.appendChild(pre);
    // eslint-disable-next-line no-console
    console.log('[probe] report:', JSON.stringify(report));
  }

  dispose(): void {
    this.holdIntent(null);
    this.panel?.remove();
  }
}
